import { IUndoRedoElement, BaseAction } from '../../undo/common/action.js';
import { IInstantiationService, ServicesAccessor } from '../../instantiation/common/instantiation.js';
import { createDecorator } from '../../instantiation/common/registry.js';
import { Emitter, Event } from '../../../base/common/events.js';
import { Disposable, IDisposable, toDisposable } from '../../../base/common/lifecycle.js';
import { IJSONSchema } from '../../../base/common/jsonSchema.js';
import type { ContextKeyExpression } from '../../contextkey/common/contextkey.js';
import { ILocalizedString } from '../../action/common/action.js';
import { Iterable } from '../../../base/common/iterator.js';
import { LinkedList } from '../../../base/common/linkedList.js';
import { TypeConstraint, validateConstraints } from '../../../base/common/types.js';
import { IKeyboardEvent } from '../../../base/browser/keyboardEvent.js';
// the op seam: the BASE owns retract-on-undo / reinstate-on-redo (H1 —
// command authors write only the live half). The LEAF identifier module,
// deliberately: importing the full operations module from here closed a
// runtime import cycle (TDZ decorator crash).
import { IOperationService } from '../../../editor/services/operations/common/operationsIdentifier.js';
import type { UndoGroup, UndoSource } from '../../undo/common/undo.js';



/**
 * Service identifier for dependency injection
 */
export const ICommandService = createDecorator<ICommandService>('commandService');

export interface ICommandEvent {
	readonly commandId: string;
	readonly args: unknown[];
}


export interface ICommandContextUser {
	isAi: false,
	// the INTERFACE, not the concrete class: the keyboard service emits
	// IKeyboardEvent (StandardKeyboardEvent has private members, so a
	// class-typed field can't hold what the service hands out)
	keyboardEvent?: IKeyboardEvent,
	undoGroup?: UndoGroup,
	undoSource?: UndoSource,
};
export interface ICommandContextAI {
	isAi: true,
	keyboardEvent?: undefined,
	/** the chat conversation the call came from (#138) — absent for MCP/agent */
	conversation?: { readonly slug: string },
	undoGroup?: UndoGroup,
	undoSource?: UndoSource,
};
export type ICommandContext = ICommandContextUser | ICommandContextAI;

export interface ExecuteCommandResult<TResult> {
	success: boolean;
	message: string;
	result: TResult;
};
export type AICommandResult = string | number | boolean;

/**
 * Service for registering and executing commands.
 * All operations should go through commands for consistency,
 * undo/redo support, and extensibility.
 */
export interface ICommandService {

	readonly onWillExecuteCommand: Event<ICommandEvent>;
	readonly onDidExecuteCommand: Event<ICommandEvent>;

	readonly onDidChange: Event<CommandServiceDidChangeEvent>;

	/**
	 * Execute a command with type inference from the command class
	 * @example executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, { property, value })
	 */
	executeCommand<TCommand extends ICommand<any, any> = ICommand<unknown, unknown>>(
		id: string,
		args: TCommand extends ICommand<infer TArgs, any> ? TArgs : unknown,
		context?: ICommandContextUser
	): Promise<ExecuteCommandResult<TCommand extends ICommand<any, infer TResult> ? TResult : unknown>>;

	/**
	 * Execute a command for AI context
	 */
	executeCommand(
		id: string,
		args: unknown,
		context: ICommandContextAI
	): Promise<ExecuteCommandResult<AICommandResult>>;

	/**
	 * Check if a command can be executed
	 */
	canExecuteCommand(id: string, args: unknown): ICommandCanExecuteReturnValue;
}


export type ICommandsMap = Map<string, ICommand>;

export interface ICommandCanExecuteReturnValue {
	canExecute: boolean;
	reason?: string;
	code?: string;
}


export interface ICommandHandler<TArgs extends unknown = unknown, TResult extends unknown | Promise<unknown> = unknown> {
	(accessor: ServicesAccessor, args: TArgs, context?: ICommandContext): TResult;
}

/**
 * Represents a command that can be executed.
 * Commands are the primary way to perform operations in the editor.
 */
export interface ICommand<
	TArgs extends unknown = unknown,
	TResult extends unknown | Promise<unknown> = unknown
> {
	/**
	 * Unique identifier for this command
	 */
	readonly id: string;

	/**
	 * Human-readable label for this command
	 */
	readonly label?: string;

	readonly metadata?: ICommandMetadata | null;

	/**
	 * Check if the command can be executed with the given arguments
	 */
	canExecute?(arg: TArgs): ICommandCanExecuteReturnValue;

	/**
	 * Execute the command with the given arguments
	 */
	execute: ICommandHandler<TArgs, TResult>;

	/**
	 * Optional: Execute the command for AI tools.
	 * If implemented, this command can be used by AI tools and must return a string response.
	 * If not implemented, the command cannot be used by AI tools.
	 */
	executeForAI?(accessor: ServicesAccessor, args: TArgs, context?: ICommandContextAI): Promise<AICommandResult> | AICommandResult;
}

export interface ICommandMetadata {
	/**
	 * NOTE: Please use an ILocalizedString. string is in the type for backcompat for now.
	 * A short summary of what the command does. This will be used in:
	 * - API commands
	 * - when showing keybindings that have no other UX
	 * - when searching for commands in the Command Palette
	 */
	readonly description: ILocalizedString | string;
	readonly args?: {
		readonly name: string;
		readonly isOptional?: boolean;
		readonly description?: string;
		// readonly constraint?: TypeConstraint;
		readonly schema?: IJSONSchema;
		readonly allowMcp?: boolean;
	};
	readonly returns?: string;
	/**
	 * What the command's AI tool TOUCHES (#127): `scene` (the inspected
	 * scene — offered to chats on any page), `local` (project/machine files)
	 * or `account` (the cloud account) — the latter two are withheld from
	 * chats on untrusted origins. Default `scene`: a command is a scene edit
	 * by design; anything reaching beyond the scene MUST declare itself.
	 */
	readonly capability?: "scene" | "local" | "account";
	/**
	 * Context-key expression gating WHERE the command's AI TOOL is offered
	 * (#138) — e.g. `aiChat.anchored` for a tool that only makes sense in an
	 * anchored conversation. Distinct from {@link precondition}: that gates
	 * palette/menu enablement of the COMMAND, this gates the tool surface —
	 * the command itself stays invocable from UI that passes explicit args.
	 */
	readonly aiWhen?: ContextKeyExpression;
	/**
	 * The name the MODEL sees, when the command id makes a poor tool name.
	 * Defaults to the id. `property.setValue` reads as `property_setValue`
	 * beside `property_read`/`property_find` — a different verb and casing for
	 * the same family — and renaming the COMMAND to fix a label would move a
	 * runtime string through 60+ call sites (UI edit paths, tests, changeset).
	 * Keep it snake_case like the other tools; the MCP surface prefixes it.
	 */
	readonly aiName?: string;
	/**
	 * OPT-IN to the command palette (`>` quick access). Default is HIDDEN: most commands
	 * are invoked with arguments from specific UI (not standalone), so visibility is an
	 * explicit declaration — combined at runtime with `canExecute()` and
	 * {@link precondition} so state-dependent commands hide when they cannot run.
	 */
	readonly palette?: boolean;
	/**
	 * Context-key expression gating WHERE the command is offered (palette; menus and AI
	 * can consult it too) — e.g. `ContextKeyExpr.has("undoRedo.hasUndo")`. Declared ON
	 * the command so every surface shares one visibility rule.
	 */
	readonly precondition?: ContextKeyExpression;
}



export interface ICommandRegistry {
	readonly onDidRegisterCommand: Event<string>;
	registerCommand(id: string, command: ICommandHandler): IDisposable;
	registerCommand(command: ICommand): IDisposable;
	registerCommandAlias(oldId: string, newId: string): IDisposable;
	getCommand(id: string): ICommand | undefined;
	getCommands(): ICommandsMap;
}

export const CommandsRegistry: ICommandRegistry = new class implements ICommandRegistry {

	private readonly _commands = new Map<string, LinkedList<ICommand>>();

	private readonly _onDidRegisterCommand = new Emitter<string>();
	readonly onDidRegisterCommand: Event<string> = this._onDidRegisterCommand.event;

	registerCommand(idOrCommand: string | ICommand, handler?: ICommandHandler): IDisposable {

		if (!idOrCommand) {
			throw new Error(`invalid command`);
		}

		if (typeof idOrCommand === 'string') {
			if (!handler) {
				throw new Error(`invalid command`);
			}
			return this.registerCommand({ id: idOrCommand, execute: handler });
		}

		// add argument validation if rich command metadata is provided
		if (idOrCommand.metadata && Array.isArray(idOrCommand.metadata.args)) {
			const constraints: Array<TypeConstraint | undefined> = [];
			for (const arg of idOrCommand.metadata.args) {
				constraints.push(arg.constraint);
			}
			const actualHandler = idOrCommand.execute || /** compat for old naming (handler) */ (idOrCommand as unknown as { handler: ICommandHandler }).handler;
			idOrCommand.execute = function (accessor, args) {
				validateConstraints([args], constraints);
				return actualHandler(accessor, args);
			};
		}

		// find a place to store the command
		const { id } = idOrCommand;

		let commands = this._commands.get(id);
		if (!commands) {
			commands = new LinkedList<ICommand>();
			this._commands.set(id, commands);
		}

		const removeFn = commands.unshift(idOrCommand);

		const ret = toDisposable(() => {
			removeFn();
			const command = this._commands.get(id);
			if (command?.isEmpty()) {
				this._commands.delete(id);
			}
		});

		// tell the world about this command
		this._onDidRegisterCommand.fire(id);

		// return markAsSingleton(ret);
		return ret;
	}

	registerCommandAlias(oldId: string, newId: string): IDisposable {
		return CommandsRegistry.registerCommand(oldId, (accessor, args) => accessor.get(ICommandService).executeCommand(newId, args));
	}

	getCommand(id: string): ICommand | undefined {
		const list = this._commands.get(id);
		if (!list || list.isEmpty()) {
			return undefined;
		}
		return Iterable.first(list);
	}

	getCommands(): ICommandsMap {
		const result = new Map<string, ICommand>();
		for (const key of this._commands.keys()) {
			const command = this.getCommand(key);
			if (command) {
				result.set(key, command);
			}
		}
		return result;
	}
};

CommandsRegistry.registerCommand('noop', () => { });





// #region Undoable Command

/**
 * A command that can be undone.
 * These commands automatically create undo/redo actions.
 */
export interface IUndoableCommand<TArgs extends unknown = unknown, TResult extends unknown = unknown> extends ICommand<TArgs, TResult> {
	/**
	 * Create an undo/redo action for this command execution
	 */
	toAction(service: IInstantiationService, args: TArgs, context?: ICommandContext): IUndoRedoElement;

	/**
	 * VIEW-STATE commands (selection, tree expand, hierarchy navigation, camera-mode
	 * toggles) set this to `false` to opt OUT of the edit undo stack: they keep their
	 * undo/redo implementation (still usable programmatically) but executing them no
	 * longer pushes an entry — otherwise clicking around buries real edits under
	 * invisible selection entries and CTRL+Z "does nothing"
	 * (plans/undo-selection-stack-issue.md).
	 */
	readonly pushToUndoStack?: boolean;
}
export function isUndoableCommand(command: ICommand): command is IUndoableCommand {
	return 'toAction' in command;
}

export interface CommandServiceDidChangeEvent {
	command: ICommand | null;
	type: 'registered' | 'unregistered';
}


export abstract class Command<TExecuteArgs extends unknown = unknown, TResult extends unknown = unknown> extends Disposable implements ICommand<TExecuteArgs, TResult> {

	constructor(
		public readonly id: string,
		public readonly label: string,
		public readonly metadata: ICommandMetadata | null = null
	) {
		super();
	}

	/**
	 * Validate if the command can execute with given arguments
	 */
	canExecute(_args: TExecuteArgs): ICommandCanExecuteReturnValue {
		return { canExecute: true };
	}

	abstract execute(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): TResult;
}


/**
 * Base class for simple undoable commands.
 * Automatically creates undo/redo actions.
 */
export abstract class UndoableCommand<TExecuteArgs extends unknown = unknown, TResult extends unknown = unknown> implements IUndoableCommand<TExecuteArgs, TResult> {

	/** see {@link IUndoableCommand.pushToUndoStack} — view-state commands override to `false` */
	readonly pushToUndoStack: boolean = true;

	constructor(
		public readonly id: string,
		public readonly label: string,
		public readonly metadata: ICommandMetadata | null = null
	) {

	}

	/**
	 * The operations THIS invocation put into the log (plans/operation-log-retraction.md).
	 * `undo()` retracts them, redo (a second `perform`) reinstates them — commands never
	 * emit inverse operations.
	 *
	 * Owned by the BASE on purpose: the registry holds ONE command instance, `toAction`
	 * copies state onto the undo element's clone, and the singleton must start clean or
	 * the NEXT invocation reinstates the previous one's ops instead of recording its own.
	 * Every command that cleared this itself was one forgotten line away from silently
	 * not recording edits (it happened: deletes stopped producing overs), so the base
	 * clears it and commands only ever ASSIGN it.
	 */
	protected emittedOpIds: readonly string[] = [];

	/**
	 * Validate if the command can execute with given arguments
	 */
	abstract canExecute(args: TExecuteArgs): ICommandCanExecuteReturnValue;

	/**
	 * Perform the command (do the operation)
	 */
	protected abstract perform(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): TResult;

	/**
	 * Undo the command — the LIVE half only (restore the scene). The op-log
	 * half (retracting {@link emittedOpIds}) is the BASE's job and runs after
	 * this settles. An ASYNC live restore must RETURN its promise: restore
	 * payloads (a deleted subtree, an assigned material's identity) are read
	 * from live state at retraction time, so retracting early ships the
	 * pre-undo state.
	 */
	protected abstract undo(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): void | Promise<void>;

	/**
	 * Execute the command (calls perform)
	 */
	execute(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): TResult {
		// context flows into perform so the ONE emission point can derive origin
		// (ai / remote / local) — see CLAUDE.md "Edits flow through COMMANDS"
		return this.perform(accessor, args, context);
	}

	executeForAI?(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): AICommandResult | Promise<AICommandResult>;
	/**
	 * Create an undo/redo action for this command
	 */
	toAction(service: IInstantiationService, args: TExecuteArgs, context?: ICommandContext): IUndoRedoElement {
		// We need to clone the command instance to preserve state between undo/redo
		const clone = Object.create(this);
		Object.assign(clone, this);
		// the ids belong to the CLONE from here on — the singleton starts fresh (see
		// the field's doc comment; forgetting this per command silently broke recording)
		this.emittedOpIds = [];

		context = context ? { ...context } : context;

		// H1 ENFORCEMENT (plans/operation-bridge.md, System hardening): the
		// LOG half of undo/redo lives HERE, not per command — hand-rolled
		// copies were forgettable and one bad copy (create.object) shipped.
		// Both calls are idempotent in the op service, so commands still
		// carrying their own retract/reinstate stay correct during migration.
		const undoAction = new BaseAction({
			label: this.label,
			sourceId: context?.undoSource?.id,
			provenance: context?.undoSource?.provenance ?? (context ? {
				origin: context.isAi ? "ai" : "local-user",
				...(context.isAi && context.conversation ? { sessionId: context.conversation.slug } : {}),
			} : undefined),
			timestamp: Date.now(),
			perform: () => service.invokeFunction(accessor => {
				// REDO: re-run the live half, then put the ORIGINAL ops back at
				// their positions (never emit newer ones)
				const operations = accessor.getIfExists(IOperationService);
				const result = clone.execute(accessor, args, context);
				const settle = () => { if (clone.emittedOpIds.length) operations?.reinstate(clone.emittedOpIds); };
				if (result && typeof (result as { then?: unknown }).then === "function") {
					return (result as Promise<unknown>).then(value => { settle(); return value; });
				}
				settle();
				return result;
			}),
			undo: () => service.invokeFunction(accessor => {
				const operations = accessor.getIfExists(IOperationService);
				const result = clone.undo(accessor, args, context) as void | Promise<void>;
				const settle = () => { if (clone.emittedOpIds.length) operations?.retract(clone.emittedOpIds); };
				if (result && typeof result.then === "function") {
					return result.then(settle);
				}
				settle();
			}),
			dispose: () => clone.disposeAction?.(),
		});
		return undoAction;
	}

	/**
	 * Optional hook: called when this command's undo/redo entry is permanently
	 * dropped from the history (cleared, evicted past the stack limit, or redo
	 * invalidated). Release objects retained for undo here — e.g. dispose a
	 * deleted hierarchy node so it stops pinning its scene resources.
	 */
	protected disposeAction?(): void;
}
